import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

import { useShareGuestPassViaEmailMutation } from "@/hooks/guestPasses";
import { GuestPassesQueryKey } from "@/hooks/useHomeData";

import FormField from "../FormField";
import FormFields from "../FormFields";
import Header from "../Header";
import Icon from "../Icon";
import { Input } from "../Input";
import Label from "../Label";
import Loader from "../Loader";
import { useNotification } from "../Notification";
import { getDaysTillExpiry, getErrorMessage, GuestPass } from ".";
import GuestPassRestrictionsAndFAQs from "./GuestPassRestictionsAndFAQs";
import {
  btnClasses,
  btnIconClasses,
  cancelBtnClasses,
  errorIconClasses,
  inputClasses,
  inputErrorClasses,
  labelClasses,
} from "./Styles";

type Props = {
  pass: GuestPass;
  onComplete: () => void;
};

const schema = z
  .object({
    firstName: z.string().min(1, { message: "First Name is required" }),
    lastName: z.string().min(1, { message: "Last Name is required" }),
    email: z.string().min(1, { message: "Email is required" }).email({
      message: "Must be a valid email",
    }),
    emailconfirm: z.string().min(1, { message: "Email is required" }).email({
      message: "Must be a valid email",
    }),
    parentCouponCodeId: z.number(),
  })
  .refine((data) => data.email === data.emailconfirm, {
    message: "Emails must match",
    path: ["emailconfirm"],
  });

type Schema = z.infer<typeof schema>;

export default function SharePassByEmail({ pass, onComplete }: Props) {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors, isValid },
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      emailconfirm: "",
      parentCouponCodeId: pass.promoCodeId,
    },
    mode: "onBlur",
  });

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const shareGuestPassViaEmailMutation = useShareGuestPassViaEmailMutation();
  const onSubmit = handleSubmit((data) => {
    shareGuestPassViaEmailMutation.mutate(data, {
      onSuccess: (result) => {
        if (result.sucId) {
          setShowSuccess(true);
          void queryClient.invalidateQueries({
            queryKey: [GuestPassesQueryKey],
          });
        }
      },
      onError: (error) => {
        notify(getErrorMessage(error.message), "error");
      },
    });
  });

  const sendDisabled = !isValid || shareGuestPassViaEmailMutation.isPending;
  const expiresInDays = getDaysTillExpiry(pass.expiresDate) ?? "";

  return (
    <>
      {!showConfirm && !showSuccess && (
        <>
          <form className="mb-8">
            <Header className="mb-4 text-[22px] text-clear-sky">
              Share Your Guest Pass
            </Header>
            <div className="mb-6 flex justify-end">
              <span className="justify-between font-['Open_Sans'] text-sm font-normal leading-none text-clear-sky">
                {expiresInDays}
              </span>
            </div>
            <FormFields className="mb-4">
              <FormField className="relative">
                <Label htmlFor="firstName" className={labelClasses}>
                  Guest First Name
                </Label>
                {errors.firstName && (
                  <Icon name="exclamation" className={errorIconClasses} />
                )}
                <Input
                  id="firstName"
                  type="text"
                  className={
                    errors.firstName ? inputErrorClasses : inputClasses
                  }
                  {...register("firstName")}
                />
                {errors.firstName && <div>{errors.firstName.message}</div>}
              </FormField>
              <FormField className="relative">
                <Label htmlFor="lastName" className={labelClasses}>
                  Guest Last Name
                </Label>
                {errors.lastName && (
                  <Icon name="exclamation" className={errorIconClasses} />
                )}
                <Input
                  id="lastName"
                  type="text"
                  className={errors.lastName ? inputErrorClasses : inputClasses}
                  {...register("lastName")}
                />
                {errors.lastName && <div>{errors.lastName.message}</div>}
              </FormField>
              <FormField className="relative">
                <Label htmlFor="email" className={labelClasses}>
                  Guest Email
                </Label>
                {errors.email && (
                  <Icon name="exclamation" className={errorIconClasses} />
                )}
                <Input
                  id="email"
                  type="email"
                  className={errors.email ? inputErrorClasses : inputClasses}
                  {...register("email")}
                />
                {errors.email && <div>{errors.email.message}</div>}
              </FormField>
              <FormField className="relative">
                <Label htmlFor="emailconfirm" className={labelClasses}>
                  Confirm Guest Email
                </Label>
                {errors.emailconfirm && (
                  <Icon name="exclamation" className={errorIconClasses} />
                )}
                <Input
                  id="emailconfirm"
                  type="email"
                  className={
                    errors.emailconfirm ? inputErrorClasses : inputClasses
                  }
                  {...register("emailconfirm")}
                />
                {errors.emailconfirm && (
                  <div>{errors.emailconfirm.message}</div>
                )}
              </FormField>
              <div className="mb-6">
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={sendDisabled}
                  className={twMerge(
                    btnClasses,
                    sendDisabled ? "bg-light-grey" : "bg-teal-700",
                  )}
                >
                  <Icon name="envelope" className={btnIconClasses} />
                  Send
                </button>
              </div>
            </FormFields>
          </form>
          <GuestPassRestrictionsAndFAQs pass={pass} />
        </>
      )}
      {showConfirm && !showSuccess && (
        <>
          <div>{`You are about to send this pass to ${getValues(
            "firstName",
          )} ${getValues("lastName")}`}</div>
          <div className="text-[14px] text-lighter-grey">
            Email Address:&nbsp;{getValues("email")}
          </div>
          <div className="mt-4 flex justify-end space-x-8">
            <button
              onClick={() => setShowConfirm(false)}
              className={cancelBtnClasses}
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={shareGuestPassViaEmailMutation.isPending}
              className={btnClasses}
            >
              <Icon name="envelope" className={btnIconClasses} />
              Send
            </button>
          </div>
          {shareGuestPassViaEmailMutation.isPending && <Loader />}
        </>
      )}
      {showSuccess && (
        <>
          <div className="mb-4 text-[22px]">
            <Icon name="check" className="mr-2 inline text-clear-sky" />
            Guess Pass Sent!
          </div>
          <span className="text-base text-lighter-grey">
            You have successfully shared a Guest Pass with
          </span>
          <span className="ml-1">{`${getValues("firstName")} ${getValues(
            "lastName",
          )}`}</span>
          <div className="mt-4 flex justify-end space-x-8">
            <button onClick={onComplete} className={btnClasses}>
              Done
            </button>
          </div>
        </>
      )}
    </>
  );
}
